// Phase 117 — distance-fading friendly nameplates.
// Projects friendly remote-player positions to screen-space and renders fading labels.
import { useEffect, useState, type CSSProperties } from 'react';

type Vec3Like = { x?: number; y?: number; z?: number };

type NameplatePlayer = {
  id?: string;
  displayName?: string;
  team?: string | null;
  position?: Vec3Like;
};

type NameplatesProps = {
  localPlayer: NameplatePlayer & { yaw?: number; pitch?: number };
  remotePlayers: Record<string, NameplatePlayer>;
};

function teamColor(team?: string | null): string {
  const t = team?.toLowerCase() ?? null;
  if (t === 'red' || t === 'blood_eagle') return '#ff3838';
  if (t === 'blue' || t === 'storm') return '#2dd4d4';
  return '#9db6c8';
}

type Projected = {
  id: string;
  name: string;
  color: string;
  screenX: number;
  screenY: number;
  opacity: number;
};

export function Nameplates({ localPlayer, remotePlayers }: NameplatesProps) {
  const [items, setItems] = useState<Projected[]>([]);
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1280,
    h: typeof window !== 'undefined' ? window.innerHeight : 720,
  }));

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const localTeam = (localPlayer.team ?? '').toLowerCase();
    if (!localTeam) {
      setItems([]);
      return;
    }
    const lpx = localPlayer.position?.x ?? 0;
    const lpy = (localPlayer.position?.y ?? 0) + 1.6;
    const lpz = localPlayer.position?.z ?? 0;
    const yaw = localPlayer.yaw ?? 0;
    const pitch = localPlayer.pitch ?? 0;
    const cosP = Math.cos(pitch);
    const fx = Math.sin(yaw) * cosP;
    const fy = Math.sin(pitch);
    const fz = -Math.cos(yaw) * cosP;
    const rx = Math.cos(yaw);
    const rz = Math.sin(yaw);
    const halfFovX = Math.tan((75 * Math.PI / 180) / 2);
    const aspect = viewport.w / viewport.h;
    const halfFovY = halfFovX / aspect;

    const next: Projected[] = [];
    for (const p of Object.values(remotePlayers)) {
      if (!p.position || p.position.x == null || p.position.z == null) continue;
      const team = (p.team ?? '').toLowerCase();
      if (team !== localTeam) continue;
      const dx = (p.position.x ?? 0) - lpx;
      const dy = (p.position.y ?? 0) + 2.2 - lpy; // above head
      const dz = (p.position.z ?? 0) - lpz;
      const forwardDist = dx * fx + dy * fy + dz * fz;
      if (forwardDist < 1) continue;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (dist > 400) continue;
      // Distance fade per spec
      let opacity = 1;
      if (dist <= 50) opacity = 1;
      else if (dist <= 200) opacity = 1 - 0.5 * ((dist - 50) / 150);
      else if (dist <= 400) opacity = 0.5 - 0.5 * ((dist - 200) / 200);
      else opacity = 0;
      if (opacity <= 0.02) continue;

      const horiz = dx * rx + dz * rz;
      const vert = dy * Math.cos(pitch) - (Math.sin(pitch) * (dx * Math.sin(yaw) + dz * -Math.cos(yaw)));
      const sx = (horiz / forwardDist) / halfFovX;
      const sy = (vert / forwardDist) / halfFovY;
      if (sx < -1.1 || sx > 1.1 || sy < -1.1 || sy > 1.1) continue;
      const screenX = (sx * 0.5 + 0.5) * viewport.w;
      const screenY = (-sy * 0.5 + 0.5) * viewport.h;
      next.push({
        id: p.id ?? '',
        name: p.displayName ?? 'Ally',
        color: teamColor(p.team),
        screenX,
        screenY,
        opacity,
      });
    }
    setItems(next);
  }, [localPlayer.position?.x, localPlayer.position?.y, localPlayer.position?.z, localPlayer.yaw, localPlayer.pitch, localPlayer.team, remotePlayers, viewport.w, viewport.h]);

  if (items.length === 0) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 18 }}>
      {items.map((item) => {
        const style: CSSProperties = {
          position: 'absolute',
          left: item.screenX,
          top: item.screenY,
          transform: 'translate(-50%, -100%)',
          padding: '1px 6px',
          background: 'rgba(4,8,18,0.55)',
          border: `1px solid ${item.color}66`,
          color: item.color,
          fontFamily: 'monospace',
          fontSize: 10,
          letterSpacing: 0.8,
          textTransform: 'uppercase',
          opacity: item.opacity,
          whiteSpace: 'nowrap',
        };
        return <div key={item.id} style={style}>{item.name}</div>;
      })}
    </div>
  );
}
